// Orchestrator — the single entry point that takes a user message and
// drives one agent turn end-to-end.
//
// Responsibilities:
//   1. Build a fresh provider from the user's settings.
//   2. Build the project-scoped tool registry.
//   3. Prepend the system prompt to the conversation history.
//   4. Append the user message to history + transcript store.
//   5. Run runAgentTurn() with an onEvent that bridges into the
//      transcript and cost stores.
//   6. Manage AbortController lifecycle for the stop button.
//
// Why a separate file from loop.ts:
//   - loop.ts is pure / provider-agnostic and has no React or store
//     dependencies. It's the runtime kernel.
//   - This file is the React-/zustand-aware adapter that wires the loop
//     into the rest of the studio. Keeping the seam clean means we can
//     reuse loop.ts in non-React contexts (tests, future Worker-driven
//     batch tasks) without dragging zustand along.

import type { Message } from './types';
import { runAgentTurn } from './loop';
import { buildSystemPrompt } from './system-prompt';
import { defaultFileStore } from '../filestore';
import { createProvider } from '../providers';
import { buildProjectTools } from '../tools';
import { useCost } from '../../state/cost';
import { useFsRevision } from '../../state/fs-revision';
import { useTranscript } from '../../state/transcript';
import type { UserSettings } from '../../state/settings';

// Tool names that mutate disk. tool_result events for these bump the
// fs-revision counter so the Canvas re-renders.
const WRITE_TOOL_NAMES = new Set(['write_file', 'edit_file']);

export interface SendMessageArgs {
  text: string;
  projectName: string;
  settings: UserSettings;
}

/**
 * Send one user message, run one agent turn (which may include multiple
 * tool-use rounds), and stream events into the transcript + cost stores.
 *
 * Returns immediately after starting the turn — caller should not await
 * unless they want to know when the turn finishes. Errors are surfaced
 * via transcript.setError(); this function does not throw.
 */
export async function sendMessage(args: SendMessageArgs): Promise<void> {
  const { text, projectName, settings } = args;
  const transcript = useTranscript.getState();
  const cost = useCost.getState();

  if (transcript.streaming) {
    // Defensive — the chat panel disables the send button while
    // streaming, but a stray hotkey or remount could still arrive.
    return;
  }

  // Compose history. The transcript store doesn't carry the system
  // prompt (system messages aren't displayed); we prepend it here for
  // every turn so the prompt always reflects the latest settings +
  // project (no staleness if the user renames the project mid-session).
  const systemPrompt = buildSystemPrompt({
    displayName: settings.displayName,
    role: settings.role,
    projectName,
  });

  const userMessage: Message = { role: 'user', content: text };

  // Push user message into the transcript first so it appears immediately.
  transcript.appendMessage(userMessage);
  transcript.setError(null);

  // Build the mutable history the loop will read from + push onto. Note
  // the transcript store stores the post-system messages only; we
  // prepend the system prompt locally for this turn.
  const history: Message[] = [
    { role: 'system', content: systemPrompt },
    ...useTranscript.getState().messages, // re-read after appendMessage
  ];

  const provider = createProvider({
    slug: settings.providerSlug,
    apiKey: settings.apiKey,
    model: settings.model || undefined,
  });

  const tools = buildProjectTools({
    fileStore: defaultFileStore,
    projectRoot: projectName,
  });

  const controller = new AbortController();
  transcript.setStreaming(true, controller);

  // Sync the visible messages from `history` (skipping the system prompt
  // at index 0). Called whenever the loop may have mutated history.
  const syncMessagesFromHistory = () => {
    useTranscript.getState().setMessages(history.slice(1));
  };

  try {
    await runAgentTurn({
      provider,
      history,
      tools,
      signal: controller.signal,
      onEvent: (e) => {
        switch (e.kind) {
          case 'assistant_text_delta':
            useTranscript.getState().appendStreamingDelta(e.text);
            break;
          case 'assistant_text_complete':
            // No state change — the full text will land in messages
            // when the loop pushes the assistant Message after stream
            // close (synced on the next event). Leaving streamingText
            // alone here keeps the live tail visible until that sync.
            break;
          case 'tool_call':
            // Loop hasn't pushed the assistant message yet (push
            // happens after streamChat returns, before tool exec).
            // We still clear streamingText so the live tail doesn't
            // double-render once the assistant block lands.
            useTranscript.setState({ streamingText: '' });
            break;
          case 'tool_result': {
            // Loop pushes tool_result AFTER this event fires, so
            // history may not yet include this result. Sync anyway —
            // the assistant message (which DID push before the tool
            // executed) is now visible, and the result will be
            // included on the next event's sync.
            syncMessagesFromHistory();
            useTranscript.setState({ streamingText: '' });
            // Find the matching tool_use in the most recent assistant
            // message to identify which tool fired; bump fs-revision
            // if it was a write. We don't have the tool name on the
            // tool_result event itself (it carries id/content/isError
            // only), so we cross-reference history.
            if (!e.isError) {
              const recentAssistant = [...history].reverse().find(
                (m) => m.role === 'assistant',
              );
              if (recentAssistant && recentAssistant.role === 'assistant') {
                const matched = recentAssistant.content.find(
                  (b) => b.type === 'tool_use' && b.id === e.id,
                );
                if (
                  matched &&
                  matched.type === 'tool_use' &&
                  WRITE_TOOL_NAMES.has(matched.name)
                ) {
                  useFsRevision.getState().bump();
                }
              }
            }
            break;
          }
          case 'usage':
            void cost.recordUsage(provider.model, e.report);
            break;
          case 'turn_complete':
            syncMessagesFromHistory();
            useTranscript.setState({ streamingText: '' });
            break;
          case 'error':
            syncMessagesFromHistory();
            useTranscript.getState().setError(e.message);
            useTranscript.setState({ streamingText: '' });
            break;
        }
      },
    });
  } catch (err) {
    // runAgentTurn catches its own errors and emits them via 'error'
    // events; if anything still escapes, surface it defensively.
    useTranscript.getState().setError(
      err instanceof Error ? err.message : String(err),
    );
  } finally {
    useTranscript.getState().setStreaming(false, null);
  }
}
